import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChildren,
  Directive,
  inject,
  input,
  TemplateRef,
} from '@angular/core';
import { ShTabsComponent } from './tabs.component';

/**
 * Marks the content of one tab: `<ng-template shTabPanel="details">`. The
 * template is only rendered while its tab is the open one (lazy), so a tab's
 * data loads when the tab is opened and is dropped when it is left.
 */
@Directive({
  selector: 'ng-template[shTabPanel]',
  standalone: true,
})
export class ShTabPanelDirective {
  readonly key = input.required<string>({ alias: 'shTabPanel' });
  readonly template = inject<TemplateRef<unknown>>(TemplateRef);
}

/**
 * The panels of an `sh-tabs` bar: `<sh-tab-panels [for]="tabs">` renders the
 * `shTabPanel` template of the open tab, wherever this element sits relative
 * to the bar.
 */
@Component({
  selector: 'sh-tab-panels',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [CommonModule],
  template: `
    <div class="p-tabpanels">
      <div class="p-tabpanel" role="tabpanel" *ngIf="activePanel() as panel">
        <ng-container *ngTemplateOutlet="panel.template"></ng-container>
      </div>
    </div>
  `,
})
export class ShTabPanelsComponent {
  /** The tab bar these panels belong to (a template reference: `#tabs`). */
  // eslint-disable-next-line @angular-eslint/no-input-rename -- [for]="tabs" is the intended public binding
  readonly tabs = input.required<ShTabsComponent>({ alias: 'for' });

  private readonly _panels = contentChildren(ShTabPanelDirective);

  protected readonly activePanel = computed(() => {
    const current = this.tabs().current();
    return this._panels().find((panel) => panel.key() === current);
  });
}
